import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Menu,
  X,
  Sparkles,
  LogIn,
  LayoutDashboard,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { label: "Benefits", href: "#benefits" },
  { label: "How it works", href: "#how" },
  { label: "Services", href: "#services" },
  { label: "FAQ", href: "#faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dashboardPath = user?.role === "admin" ? "/admin" : "/agent";

  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,box-shadow] duration-300",
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border/60 shadow-soft"
          : "bg-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <img src={process.env.PUBLIC_URL + "/logo.png"} alt="1Rupee Clinic" className="h-11 w-auto object-contain" />
          <div className="leading-none border-l border-border/70 pl-2.5">
            <div className="font-display font-bold text-sm tracking-tight text-foreground">
              1Rupee <span className="text-primary">Clinic</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
              Membership Portal
            </div>
          </div>
        </Link>

        {pathname === "/" && (
          <nav className="hidden md:flex items-center gap-1">
            {nav.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-3.5 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>
        )}

        <div className="hidden md:flex items-center gap-2">
          <Link to="/verify">
            <Button variant="ghost" size="sm" className="text-sm">
              <ShieldCheck className="h-4 w-4" /> Verify
            </Button>
          </Link>
          {user ? (
            <>
              <Link to={dashboardPath}>
                <Button variant="outline" size="sm" className="text-sm">
                  <LayoutDashboard className="h-4 w-4" />
                  {user.role === "admin" ? "Admin" : "Agent"} dashboard
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
              >
                <LogOut className="h-4 w-4" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-sm">
                  <LogIn className="h-4 w-4" /> Agent / Admin
                </Button>
              </Link>
              <Link to="/generate">
                <Button size="sm" className="bg-gradient-primary text-primary-foreground hover:opacity-95 shadow-elegant">
                  <Sparkles className="h-4 w-4" />
                  Get Membership
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background/80"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-lg animate-fade-in">
          <div className="px-5 py-4 flex flex-col gap-1">
            {pathname === "/" &&
              nav.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 rounded-lg text-sm font-medium text-foreground hover:bg-muted"
                >
                  {item.label}
                </a>
              ))}
            <div className="flex flex-col gap-2 pt-2">
              <Link to="/verify" onClick={() => setOpen(false)}>
                <Button variant="outline" className="w-full">
                  <ShieldCheck className="h-4 w-4" /> Verify card
                </Button>
              </Link>
              {user ? (
                <>
                  <Link to={dashboardPath} onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full">
                      <LayoutDashboard className="h-4 w-4" /> Dashboard
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      logout();
                      setOpen(false);
                      navigate("/");
                    }}
                  >
                    <LogOut className="h-4 w-4" /> Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full">
                      <LogIn className="h-4 w-4" /> Agent / Admin login
                    </Button>
                  </Link>
                  <Link to="/generate" onClick={() => setOpen(false)}>
                    <Button className="w-full bg-gradient-primary text-primary-foreground shadow-elegant">
                      <Sparkles className="h-4 w-4" /> Get Membership
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
